import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api";

import { useGetUser } from "@/users/v3";
import useGetUserClassrooms from "@/classrooms/hooks/useGetUserClassrooms";
import { API_GATEWAY_HOST } from "@/base/base.constants";
import { LunaChip } from "../FilterPanel/LunaFilterPanel/LunaFilterPanel.utils";
import type { QueryKey } from "@tanstack/react-query";

const SUBJECT_SLUG_MAP: Record<string, string> = {
  "english-arts-reading": "ELA",
  science: "SCI",
  "social-studies": "SS",
};

interface UseTrafficCopProps {
  enabled?: boolean;
  queryKey?: QueryKey;
  prompt?: string;
  chips?: LunaChip[];
  isExperimentModeRandomSelection?: boolean;
  messageHistory?: Record<string, any>;
}

export const useTrafficCopQuery = ({
  enabled = false,
  prompt = "",
  chips,
  isExperimentModeRandomSelection = true,
  messageHistory = {},
}: UseTrafficCopProps): any => {
  const { data: userData } = useGetUser();
  const { data: userClassrooms } = useGetUserClassrooms({
    userUniqueId: userData?.unique_id,
    enabled,
  });

  const userClassroomGradeLevels = useMemo(() => {
    if (userClassrooms) {
      return [
        ...new Set(
          userClassrooms.results
            .map((classroom) => classroom.grade)
            .filter((grade) => grade != null),
        ),
      ];
    } else {
      return [];
    }
  }, [userClassrooms]);

  const userClassroomSubjects = useMemo(() => {
    if (!userClassrooms) return [];

    const subjectStrings = userClassrooms.results.flatMap(
      (classroom) =>
        classroom.subjects?.map(
          (subject) => SUBJECT_SLUG_MAP[subject.slug] ?? subject.title,
        ) ?? [],
    );

    return [...new Set(subjectStrings)];
  }, [userClassrooms]);

  const payload = {
    prompt,
    data: {
      chips,
      user_attributes: {
        grade_levels: userClassroomGradeLevels,
        subjects: userClassroomSubjects,
        region_codes: userData?.state ? [userData?.state] : [],
      },
      experiment_mode: isExperimentModeRandomSelection
        ? "random_selection"
        : null,
      message_history:
        Object.keys(messageHistory).length === 0 ? null : messageHistory,
    },
  };

  const options = {
    basePath: `${API_GATEWAY_HOST}/seti-traffic-cop/`,
  };
  return useQuery({
    enabled,
    queryKey: ["seti-traffic-cop", prompt],
    queryFn: async () => api(options).post("chat", payload, {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
